import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const CACHES_SOURCE = 'https://w3c.github.io/ServiceWorker/#cache-interface';
const RFC9111_SOURCE = 'https://www.rfc-editor.org/rfc/rfc9111.html';

/**
 * Cache-key normalization & Vary handling (desync-cache-fingerprinting §2).
 *
 * SCOPE, STATED PLAINLY: the Cache API matching rule is SPEC-DEFINED — a
 * lookup matches when the URL string matches, so path case and query order are
 * significant by construction. Results here are expected to be IDENTICAL
 * across conformant browsers, so this is not a fingerprint. It is also NOT the
 * page-side half of Kettle's CDN cache-key research: that research concerns
 * server-side caches and request smuggling, where the cache key is
 * vendor-defined and unkeyed headers can probe it. Nothing here observes a
 * CDN; it is a conformance check and an environment report.
 *
 * What a static page CAN observe about its own cache, and what this measures:
 *
 *  1. Cache API availability and whether its key handling matches the spec
 *     (path case, trailing slash and query order stay distinct). A divergence
 *     would indicate a non-conformant or hooked implementation and is worth
 *     reporting; a conformant result says nothing about the device.
 *  2. Timing as a hit/miss proxy: repeat fetches of a known-cacheable asset and
 *     compare round trips. Coarse, but a stable per-environment property.
 *
 * (A Vary/Accept-Encoding round-trip used to live here. It was removed:
 * `Accept-Encoding` is a FORBIDDEN request header, so script cannot vary it and
 * both requests carried the browser's own encodings. Verified in production:
 * the "wide" and "narrow" requests came back with identical encodings, so the
 * check measured nothing.)
 *
 * Nothing here injects a request at a third party; every request is same-origin
 * to this site.
 */
export const cacheProbe: Probe = {
  id: 'cache',
  label: 'Cache behaviour',
  layer: 'network',
  tier: 'client',
  category: 'browser',
  description: 'Cache API spec conformance and hit/miss timing',
  sources: [CACHES_SOURCE, RFC9111_SOURCE],
  budgetMs: 6000,

  async run(): Promise<ProbeResult> {
    const signals: Record<string, unknown> = {};
    let anyPart: Record<string, unknown> = {};

    // --- 1. Cache API key normalization -----------------------------------
    const cacheApi = await probeCacheKeyNormalization();
    Object.assign(signals, cacheApi.signals);

    // --- 2. cache hit/miss timing -----------------------------------------
    const timing = await probeCacheTiming();
    Object.assign(signals, timing.signals);

    // Only structural facts go into the part: whether the Cache API exists and
    // whether its key normalization is strict. Timing is deliberately excluded
    // (it tracks network conditions).
    anyPart = {
      cacheApiAvailable: cacheApi.available,
      queryOrderSensitive: cacheApi.queryOrderSensitive,
      pathCaseSensitive: cacheApi.pathCaseSensitive,
    };

    return {
      signals: signals as ProbeResult['signals'],
      parts: { browserFeatures: anyPart },
    };
  },
};

/** 1. Does the browser's Cache API normalize cache keys? */
async function probeCacheKeyNormalization(): Promise<{
  available: boolean;
  pathCaseSensitive: boolean | null;
  queryOrderSensitive: boolean | null;
  trailingSlashSensitive: boolean | null;
  signals: Record<string, ReturnType<typeof ok> | ReturnType<typeof na>>;
}> {
  const signals: Record<string, ReturnType<typeof ok> | ReturnType<typeof na>> = {};
  if (typeof caches === 'undefined') {
    signals.cacheApi = na<boolean>('unsupported', 'client', 'Cache API unavailable');
    return {
      available: false,
      pathCaseSensitive: null,
      queryOrderSensitive: null,
      trailingSlashSensitive: null,
      signals,
    };
  }
  signals.cacheApi = ok(true, 'client');

  // A private cache name so we never collide with anything on the origin.
  const CACHE_NAME = 'fpl-cache-probe-v1';
  let cache: Cache | null = null;
  try {
    cache = await caches.open(CACHE_NAME);
  } catch {
    signals.cacheApiOpen = na<boolean>('error', 'client', 'caches.open failed');
    return {
      available: true,
      pathCaseSensitive: null,
      queryOrderSensitive: null,
      trailingSlashSensitive: null,
      signals,
    };
  }

  // Build a canonical entry, then probe whether lookups for URL variants match.
  const canonical = new URL('/fpl-probe-resource?a=1&b=2', location.href);
  const body = 'fpl-cache-probe-body';
  try {
    await cache.put(
      canonical,
      new Response(body, { headers: { 'content-type': 'text/plain' } }),
    );
  } catch {
    signals.cachePut = na<boolean>('error', 'client', 'cache.put failed');
    return {
      available: true,
      pathCaseSensitive: null,
      queryOrderSensitive: null,
      trailingSlashSensitive: null,
      signals,
    };
  }
  signals.cachePut = ok(true, 'client');

  // Does a path-case variant match the canonical entry?
  const caseVariant = new URL('/FPL-PROBE-RESOURCE?a=1&b=2', location.href);
  const caseMatch = await cache.match(caseVariant);

  // Does a query-reordered variant match?
  const queryVariant = new URL('/fpl-probe-resource?b=2&a=1', location.href);
  const queryMatch = await cache.match(queryVariant);

  // Does a trailing-slash variant match?
  const slashVariant = new URL('/fpl-probe-resource/?a=1&b=2', location.href);
  const slashMatch = await cache.match(slashVariant);

  // Per spec the Cache API matches by URL *string* (with some normalization
  // for the fragment and default ports), so case / query-order ARE distinct on
  // every conformant implementation. These booleans are a conformance check,
  // not a cross-browser differentiating signal.
  const pathCaseSensitive = caseMatch === undefined;
  const queryOrderSensitive = queryMatch === undefined;
  const trailingSlashSensitive = slashMatch === undefined;

  signals.pathCaseSensitive = ok(pathCaseSensitive, 'derived');
  signals.queryOrderSensitive = ok(queryOrderSensitive, 'derived');
  signals.trailingSlashSensitive = ok(trailingSlashSensitive, 'derived');

  // Clean up so we do not leave a stray cache entry on the visitor's device.
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    /* best effort */
  }

  return {
    available: true,
    pathCaseSensitive,
    queryOrderSensitive,
    trailingSlashSensitive,
    signals,
  };
}

/** 2. Coarse cache hit/miss timing. Reported, never scored, never in a part. */
async function probeCacheTiming(): Promise<{
  signals: Record<string, ReturnType<typeof ok> | ReturnType<typeof na>>;
}> {
  const signals: Record<string, ReturnType<typeof ok> | ReturnType<typeof na>> = {};
  const url = `/_headers?probe=timing&t=${Date.now()}`;
  try {
    const t0 = performance.now();
    const first = await fetch(url, { cache: 'no-store' });
    await first.arrayBuffer();
    const firstMs = performance.now() - t0;

    // Second request with default cache mode: may be a hit if the response is
    // cacheable. Compare the round trips.
    const t1 = performance.now();
    const second = await fetch(url, { cache: 'default' });
    await second.arrayBuffer();
    const secondMs = performance.now() - t1;

    signals.firstFetchMs = ok(sig(firstMs, 2), 'client');
    signals.secondFetchMs = ok(sig(secondMs, 2), 'client');
    // A second request much faster than the first hints at a cache hit.
    signals.secondFaster = ok(secondMs < firstMs, 'derived');
  } catch (err) {
    signals.cacheTiming = na<boolean>('error', 'client', String((err as Error)?.message ?? err));
  }
  return { signals };
}

registerProbe(cacheProbe);
